import React, { useCallback, useEffect, useState } from "react";
import { Shield, Pause, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { floorStatus, floorHalt, floorResume, floorQueuedCancel, cryptoTriggerCancel, cryptoTriggerRenew } from "../lib/api";

const ct = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " CT" : "");

export const FloorCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => floorStatus().then(setSt).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const toggle = async () => {
    setBusy(true);
    try { const r = await (st?.halted ? floorResume() : floorHalt()); toast.success(r.report); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Floor update failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="floor-card">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[#00F0FF] font-bold flex items-center gap-2"><Shield className="w-4 h-4" /> Floor Chief</p>
        <span data-testid="floor-status" className={`text-xs font-bold px-2 py-0.5 rounded-full ${st?.halted ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>
          {st ? (st.halted ? "HALTED" : "RUNNING") : "…"}
        </span>
        <button onClick={toggle} disabled={busy || !st} data-testid="floor-halt-toggle"
          className={`ml-auto px-3 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1 ${st?.halted ? "text-emerald-300 border-emerald-400/40" : "text-rose-300 border-rose-400/40"}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : st?.halted ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          {st?.halted ? "Resume" : "Halt"}
        </button>
      </div>
      {st?.halt_reason && st.halted && <p className="text-xs text-rose-300/80" data-testid="floor-halt-reason">{st.halt_reason}</p>}
      {st?.gates && (
        <p data-testid="floor-gates-line" className="text-xs font-mono text-[#00F0FF]/80">
          long gate {st.gates.long} · momentum gate {st.gates.momentum} · short gate {st.gates.short}
        </p>)}
      {st?.bad_kill_scorecard && <p data-testid="floor-bad-kill-scorecard" className="text-[11px] text-amber-200/80">{st.bad_kill_scorecard}</p>}
      {st?.commodities_broker && (
        <p data-testid="floor-commodities-broker" className={`text-xs ${st.commodities_broker.connected ? "text-emerald-300/80" : "text-amber-300"}`}>{st.commodities_broker.line}</p>)}
      {!!st?.queued_orders?.length && (
        <div className="space-y-1" data-testid="floor-queued-orders">
          <p className="text-[11px] text-cyan-200/50 font-semibold">Queued stock orders</p>
          {st.queued_orders.map((o) => (
            <div key={o.id} className="text-xs flex items-center gap-2 text-cyan-100/85" data-testid={`floor-queued-${o.id}`}>
              <span className="font-bold text-cyan-50">{o.symbol}</span>
              <span>×{o.qty} @ {o.entry} · stop {o.stop} · target {o.target}</span>
              <span className="px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 text-[10px] font-bold">{o.status === "armed for open" ? "armed for open" : o.status}</span>
              <span className="text-cyan-200/40">{o.session_date}</span>
              {o.status === "armed for open" && <button onClick={() => floorQueuedCancel(o.id).then(load)} data-testid={`floor-queued-cancel-${o.id}`} className="ml-auto text-rose-300/80 hover:text-rose-300 text-[10px]">cancel</button>}
            </div>))}
        </div>)}
      {!!st?.crypto_triggers?.length && (
        <div className="space-y-1" data-testid="floor-crypto-triggers">
          <p className="text-[11px] text-cyan-200/50 font-semibold">Armed crypto triggers</p>
          {st.crypto_triggers.map((t) => (
            <div key={t.id} className="text-xs flex flex-wrap items-center gap-2 text-cyan-100/85" data-testid={`floor-trigger-${t.id}`}>
              <span className="font-bold text-cyan-50">{t.symbol} {t.side}</span>
              <span>{t.condition} · stop {t.stop} · targets {t.targets.join(" / ")}</span>
              {t.dry_run && <span className="text-[10px] text-cyan-300/70">dry-run</span>}
              <span className="text-cyan-200/40">expires {ct(t.expires_at)}</span>
              <span className="ml-auto flex gap-2">
                <button onClick={() => cryptoTriggerRenew(t.id).then(load)} data-testid={`floor-trigger-renew-${t.id}`} className="text-cyan-300/80 text-[10px]">renew</button>
                <button onClick={() => cryptoTriggerCancel(t.id).then(load)} data-testid={`floor-trigger-cancel-${t.id}`} className="text-rose-300/80 text-[10px]">cancel</button>
              </span>
            </div>))}
        </div>)}
      <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="floor-log">
        {!st?.log?.length && <p className="text-xs text-cyan-200/40">No floor activity yet.</p>}
        {st?.log?.map((l) => (
          <div key={l.id} className="text-xs flex gap-2 text-cyan-100/80" data-testid={`floor-log-${l.id}`}>
            <span className="nx-mono text-cyan-200/40 shrink-0">{ct(l.ts)}</span>
            <span className="text-[#00F0FF] shrink-0">{l.agent}</span>
            <span className="truncate">{l.outcome || l.decision || l.request}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
